import { makeAutoObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styles from './overlay.css';

class OverlayStore {
  currentFloatingBanner: React.ReactNode = null;
  readonly dialogs: { id: number; element: React.ReactNode }[] =
    observable.array([], { deep: false });
  private dialogCounter = 0;

  constructor() {
    makeAutoObservable(this);
  }

  openDialog(dialog: React.ReactNode) {
    const id = this.dialogCounter++;
    this.dialogs.push({ id, element: dialog });
    return id;
  }

  closeDialog(id: number) {
    this.dialogs.splice(
      this.dialogs.findIndex((d) => d.id === id),
      1,
    );
  }

  openFloatingBanner(banner: React.ReactNode) {
    if (!this.currentFloatingBanner) {
      this.currentFloatingBanner = banner;
    }
  }

  closeFloatingBanner() {
    this.currentFloatingBanner = null;
  }

  get isDialogOpen() {
    return this.dialogs.length > 0;
  }
}

export const overlayStore = new OverlayStore();

// This component should be wrapped at the highest level of the application,
// serving as the **relative root** for overlays such as dialogs and floating banners.
//
// It ensures that:
// - Floating banners and dialogs are rendered at the top level of the app.
// -The `childrenContainer` section correctly takes up the remaining space below overlays.
export const OverlayHost = observer(({ children }: React.PropsWithChildren) => (
  <div className={styles.container}>
    {overlayStore.currentFloatingBanner}
    {overlayStore.dialogs.map((dialogData) => (
      <React.Fragment key={dialogData.id}>{dialogData.element}</React.Fragment>
    ))}
    <div className={styles.childrenContainer}>{children}</div>
  </div>
));

export function mount(element: React.ReactElement) {
  const dialogId = overlayStore.openDialog(element);
  return () => {
    overlayStore.closeDialog(dialogId);
  };
}
